import { useCallback, useRef } from 'react';

import { Feature } from 'config/features';
import { atom, useAtomValue } from 'jotai';

import { useFeature } from 'hooks/useFeature';

import { filterStore } from 'data/Atom/Store';

import { toggleValueInSet } from 'utility/Toggle/toggleValueInSet';

const changedFilterListAtom = atom(new Set<string>());

const emptyCallback = () => {};
const nothingChanged = () => false;

/**
 * used to build changed filter logic for simple checkboxes
 */
export const useChangedFilterList = () => {
    const isFeatureActive = useFeature(Feature.ManualFilterSubmit);
    const setChangedFilterList = useCallback(
        (currentList: Set<string>) =>
            filterStore.set(changedFilterListAtom, currentList),
        []
    );

    const isFilterChanged = useCallback((filter: string) => {
        const changedFilterList = filterStore.get(changedFilterListAtom);

        return changedFilterList.has(filter);
    }, []);

    const onToggle = useCallback(
        (filter: string) => {
            const changedFilterList = filterStore.get(changedFilterListAtom);
            setChangedFilterList(toggleValueInSet(changedFilterList, filter));
        },
        [setChangedFilterList]
    );

    const onClear = useCallback(
        (selectedFilterValues: () => string[]) => {
            selectedFilterValues().forEach((filter) => {
                onToggle(filter);
            });
        },
        [onToggle]
    );

    if (!isFeatureActive) {
        return {
            isFilterChanged: nothingChanged,
            onToggle: emptyCallback,
            onClear: emptyCallback,
        };
    }

    return {
        isFilterChanged,
        onToggle,
        onClear,
    };
};

/**
 * returns the current count of changed filter
 */
export const useChangedFilterCount = () => {
    const isFeatureActive = useFeature(Feature.ManualFilterSubmit);
    const changedFilter = useAtomValue(changedFilterListAtom);

    return isFeatureActive ? changedFilter.size : 0;
};

/**
 * resets the changed filter
 */
export const useResetChangedFilterList = () => {
    const isFeatureActive = useFeature(Feature.ManualFilterSubmit);

    const resetChangedFilterList = useCallback(
        () => filterStore.set(changedFilterListAtom, new Set()),
        []
    );
    return isFeatureActive ? resetChangedFilterList : emptyCallback;
};

/**
 * used to build changed filter logic for single or complex value sections like radio buttons
 */
export const useChangedFilterListForSection = (
    sectionName: string,
    currentValueSelector: () => string | undefined
) => {
    const isFeatureActive = useFeature(Feature.ManualFilterSubmit);
    const { isFilterChanged, onToggle } = useChangedFilterList();

    const originalValue = useRef<string>();

    const isFilterChanged_ = useCallback(
        (filter: string) =>
            isFilterChanged(sectionName) &&
            // mark original value as changed
            (filter === originalValue.current ||
                // mark current selected value as changed
                filter === currentValueSelector()),
        [currentValueSelector, isFilterChanged, sectionName]
    );

    const onSelect = useCallback(
        (filter: string) => {
            if (!isFilterChanged(sectionName)) {
                // filter section is currently unchanged
                originalValue.current = currentValueSelector();
                onToggle(sectionName);
            } else if (filter === originalValue.current) {
                // filter section is currently changed, but is reverted
                onToggle(sectionName);
            }
        },
        [isFilterChanged, sectionName, currentValueSelector, onToggle]
    );

    const onClear = useCallback(() => {
        if (isFilterChanged(sectionName)) {
            // filter section is currently marked changed
            if (originalValue.current === undefined) {
                // originally there was no selection, so clear corresponds to revert
                onToggle(sectionName);
            }
        } else {
            // filter section is currently marked unchanged
            const currentValue = currentValueSelector();
            if (currentValue !== undefined) {
                // currently there is one filter selected, so clear is a change
                originalValue.current = currentValue;
                onToggle(sectionName);
            }
        }
    }, [isFilterChanged, sectionName, onToggle, currentValueSelector]);

    if (!isFeatureActive) {
        return {
            isFilterChanged: nothingChanged,
            onSelect: emptyCallback,
            onClear: emptyCallback,
        };
    }

    return {
        isFilterChanged: isFilterChanged_,
        onSelect,
        onClear,
    };
};

/**
 * used to build changed filter logic for single or complex value sections like slider
 */
export const useChangedFilterListForSlider = (
    sectionName: string,
    currentValueSelector: () => string
) => {
    const isFeatureActive = useFeature(Feature.ManualFilterSubmit);
    const { isFilterChanged, onToggle } = useChangedFilterList();

    const originalValue = useRef<string>();

    const isFilterChanged_ = useCallback(
        () => isFilterChanged(sectionName),
        [isFilterChanged, sectionName]
    );

    const onSelect = useCallback(
        (filter: string) => {
            const currentValue = currentValueSelector();
            if (!isFilterChanged(sectionName)) {
                originalValue.current = currentValue;
            }
            if (
                (isFilterChanged(sectionName) &&
                    filter === originalValue.current) ||
                (!isFilterChanged(sectionName) &&
                    filter !== originalValue.current)
            ) {
                onToggle(sectionName);
            }
        },
        [isFilterChanged, sectionName, currentValueSelector, onToggle]
    );

    const onClear = useCallback(() => {
        const currentValue = currentValueSelector();
        if (
            !isFilterChanged(sectionName) &&
            (originalValue.current === undefined ||
                originalValue.current !== currentValue)
        ) {
            originalValue.current = currentValue;
            if (originalValue.current !== 'until') {
                onToggle(sectionName);
            }
        } else if (
            currentValue !== 'until' &&
            ((isFilterChanged(sectionName) &&
                originalValue.current !== currentValue) ||
                (!isFilterChanged(sectionName) &&
                    currentValue === originalValue.current))
        ) {
            onToggle(sectionName);
        }
    }, [isFilterChanged, sectionName, onToggle, currentValueSelector]);

    if (!isFeatureActive) {
        return {
            isFilterChanged: nothingChanged,
            onSelect: emptyCallback,
            onClear: emptyCallback,
        };
    }

    return {
        isFilterChanged: isFilterChanged_,
        onSelect,
        onClear,
    };
};
